import { useState } from "react";
import { TabGroup } from "@headlessui/react";
import { classes } from "lib/actions/util";
import { useMeasureRef } from "lib/hooks/useMeasureRef";
import { useViewsMode } from "lib/contexts/SpatialContexts/ViewsModeContext";
import { useViewActions } from "lib/hooks/views/useViewActions";
import { ViewerTab } from "lib/actions/views/resolveViewerTab";
import SliderTabBar from "components/elements/Tabs/SliderTabBar";
import SliderTab from "components/elements/Tabs/SliderTab";
import UndoSvg from "components/icons/24/other/undo.svg";
import ImageSvg from "components/icons/24/AssetTypeIcons/asset_type-image.svg";
import EditSvg from "components/icons/24/actions/action-edit.svg";
import Button from "components/elements/Button/Button";
import CloseButton from "components/elements/Button/CloseButton";
import ToolbarTooltip from "components/elements/CesiumToolbar/ToolbarTooltip";

const tabs: ViewerTab[] = ["snapshot", "3d"];

// Kept clear on the right on the Snapshot tab so the bar never covers the
// snapshot viewer's zoom controls in the bottom right. Matches px-4 plus the
// min-w-[160px] spacer.
const ZOOM_CONTROLS_CLEARANCE = 176;
const GUTTER = 16;

// Width available to the bar below which it drops the name and shows icons
// only, to stay on one row, since the dock above it is offset for one row.
const COMPACT_BELOW_WIDTH = 440;

export default function ViewModeBar() {
  const {
    loadedView,
    hasViewChanged,
    activeTab,
    pickTab,
    isEditingAnnotations,
    startEditingAnnotations,
  } = useViewsMode();
  const { restoreView, exitView } = useViewActions();
  const [viewerWidth, setViewerWidth] = useState(Infinity);
  const measureRef = useMeasureRef((bb) => setViewerWidth(bb.width));

  const hasSnapshot = !!loadedView?.snapshotId;
  const hasAnnotations = !!loadedView?.snapshotAnnotationJson;
  const isSnapshotTab = activeTab === "snapshot";
  const canAnnotate = isSnapshotTab && loadedView?.canManage;
  const canRestore = activeTab === "3d" && hasViewChanged;
  const annotateLabel = hasAnnotations ? "Edit" : "Annotate";
  const annotateTip = hasAnnotations ? "Edit annotations" : "Annotate";
  const rightClearance = isSnapshotTab ? ZOOM_CONTROLS_CLEARANCE : GUTTER;
  const availableWidth = viewerWidth - rightClearance - GUTTER;
  const isCompact = availableWidth < COMPACT_BELOW_WIDTH;

  // The annotation editor takes over the whole viewer and has its own close.
  if (isEditingAnnotations) return null;

  // Overlays the viewer rather than sitting above it, so the viewer is the
  // same size whether or not a view is open and snapshots fit it exactly. The
  // new toolbar's dock rises above it while a view is open.
  return (
    <div
      ref={measureRef}
      className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex px-4"
    >
      {/* The spacers share the spare room, so the bar is centred until the
          right one reaches the zoom controls' width, then shifts left. */}
      <div className="flex-1" />
      <div className="pointer-events-auto flex min-w-0 select-none items-center gap-2 overflow-hidden rounded-xl bg-white p-1.5 shadow-md">
        <TabGroup
          selectedIndex={tabs.indexOf(activeTab)}
          onChange={(index) => pickTab(tabs[index])}
        >
          <SliderTabBar
            layoutGroupId="viewerTabs"
            className={{ root: "shrink-0 p-1" }}
          >
            <ToolbarTooltip label="Snapshot">
              <SliderTab className="w-10 text-sm" disabled={!hasSnapshot}>
                <ImageSvg className="mx-auto h-5 w-5" aria-hidden />
                <span className="sr-only">Snapshot</span>
              </SliderTab>
            </ToolbarTooltip>
            <SliderTab className="w-10 text-sm">3D</SliderTab>
          </SliderTabBar>
        </TabGroup>
        {!isCompact && (
          <div className="min-w-0 max-w-xs truncate px-1">
            {loadedView?.name ?? "Untitled view"}
          </div>
        )}
        {canAnnotate && (
          <Button
            className="flex shrink-0 items-center gap-1.5 px-3"
            type="button"
            slim
            title={isCompact ? annotateTip : undefined}
            onClick={startEditingAnnotations}
          >
            {isCompact ? (
              <>
                <EditSvg className="h-5 w-5" aria-hidden />
                <span className="sr-only">{annotateTip}</span>
              </>
            ) : (
              annotateLabel
            )}
          </Button>
        )}
        {canRestore && (
          <Button
            className="flex shrink-0 items-center gap-1.5 px-2"
            type="button"
            slim
            title={isCompact ? "Restore" : undefined}
            onClick={restoreView}
          >
            <UndoSvg className="h-5 w-5" aria-hidden />
            <span className={classes(isCompact && "sr-only")}>Restore</span>
          </Button>
        )}
        <CloseButton
          className="mr-1 shrink-0 p-2"
          type="button"
          aria-label="Close view"
          onClick={exitView}
        />
      </div>
      <div className={classes("flex-1", isSnapshotTab && "min-w-[160px]")} />
    </div>
  );
}
